import { useMemo } from 'react';
import type { Demanda } from '../../../models';
import { normalizeObservadorData } from '../utils/demandUtils';

interface DemandaExtended extends Demanda {
  demandados?: any[];
}

interface UseDemandViewHelpersProps {
  demanda: DemandaExtended;
  currentUser: any;
  setores?: any[];
}

/**
 * Hook que fornece funções auxiliares para a visualização de demandas
 */
export const useDemandViewHelpers = ({
  demanda,
  currentUser,
  setores = []
}: UseDemandViewHelpersProps) => {
  
  /**
   * Verifica se o usuário atual é apenas observador (não tem outros papéis)
   */
  const isOnlyObserver = useMemo(() => {
    if (!currentUser || !demanda) return false;

    const isObserver = demanda.observadores?.some(obs => {
      try {
        const { user } = normalizeObservadorData(obs);
        return user.id === currentUser.id;
      } catch {
        return false;
      }
    });

    const isCreator = demanda.criado_por_fk === currentUser.id;
    const isDemandant = demanda.demandante_fk === currentUser.id;
    const isAssigned =
      demanda.demandados?.some(user => user.id === currentUser.id) ||
      (demanda.demandado &&
        !Array.isArray(demanda.demandado) &&
        demanda.demandado.id === currentUser.id) ||
      (Array.isArray(demanda.demandado) &&
        demanda.demandado.some(user => user.id === currentUser.id));

    return isObserver && !isCreator && !isDemandant && !isAssigned;
  }, [currentUser, demanda]);

  /**
   * Nome do setor que enviou/criou a demanda (via setor_enviou_fk)
   */
  const setorOrigemNome = useMemo(() => {
    const setor = setores.find(s => s.id === demanda.setor_enviou_fk);
    return setor?.nome_setor || 'Não informado';
  }, [setores, demanda.setor_enviou_fk]);

  /**
   * Conta observadores válidos da demanda
   */
  const validObserversCount = useMemo(() => {
    if (!demanda.observadores || demanda.observadores.length === 0) return 0;
    
    return demanda.observadores.filter(obs => {
      try {
        normalizeObservadorData(obs);
        return true;
      } catch {
        return false;
      }
    }).length;
  }, [demanda.observadores]);

  /**
   * Lista de observadores válidos da demanda
   */
  const validObservers = useMemo(() => {
    if (!demanda.observadores || demanda.observadores.length === 0) return [];
    
    return demanda.observadores
      .filter(obs => {
        try {
          normalizeObservadorData(obs);
          return true;
        } catch {
          return false;
        }
      })
      .map(obs => {
        const { user } = normalizeObservadorData(obs);
        return user;
      });
  }, [demanda.observadores]);

  /**
   * Verifica se o usuário pode remover um anexo
   */
  const canDeleteAttachment = useMemo(() => {
    return (anexo: any) => {
      if (!currentUser) return false;
      
      // Criador da demanda ou usuário que fez upload
      return (
        currentUser.id === (demanda.criador?.id || demanda.demandante?.id) ||
        currentUser.id === anexo.usuario_criador_fk
      );
    };
  }, [currentUser, demanda]);

  /**
   * Formata texto de observadores para exibição
   */
  const observersDisplayText = useMemo(() => {
    const count = validObserversCount;
    if (count === 0) return 'Nenhum observador';
    return `${count} observador${count !== 1 ? 'es' : ''}`;
  }, [validObserversCount]);

  return {
    isOnlyObserver,
    setorOrigemNome,
    validObserversCount,
    validObservers,
    canDeleteAttachment,
    observersDisplayText
  };
};